

import React, { useEffect, useState } from "react";
import { DOCUMENT_EXTENSIONS } from "../constants";
import { EmptyField, FieldShell } from "../components/FieldShell";
import {
  fileExt,
  getFileName,
  getFilePreviewUrl,
  isHttpUrl,
  isImageUrl,
  normalizeFileItems,
} from "../utils/fileUtils";

export function FileField({ label, column, raw }) {
  const files = normalizeFileItems(raw);

  if (!files.length) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <FieldShell label={label} column={column}>
      <div className="safety-row-details__file-list">
        {files.map((file, fileIndex) => {
          const { url, name, ext } = file;

          if (!isHttpUrl(url)) {
            return (
              <span
                key={`${name}-${fileIndex}`}
                className="safety-row-details__dash"
              >
                —
              </span>
            );
          }

          if (isImageUrl(url)) {
            return (
              <img
                key={`${url}-${fileIndex}`}
                className="safety-row-details__image"
                src={url}
                alt={name}
                title={name}
                onClick={() =>
                  window.open(url, "_blank", "noopener,noreferrer")
                }
                onError={(event) => {
                  event.currentTarget.replaceWith(
                    Object.assign(document.createElement("a"), {
                      href: url,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      className: "safety-row-details__link",
                      textContent: name,
                    }),
                  );
                }}
              />
            );
          }

          if (DOCUMENT_EXTENSIONS.includes(ext)) {
            return (
              <a
                key={`${url}-${fileIndex}`}
                href={getFilePreviewUrl(url)}
                target="_blank"
                rel="noopener noreferrer"
                className="safety-row-details__file-link"
                title={name}
              >
                <span className="safety-row-details__file-badge">{ext}</span>
                <span className="safety-row-details__file-name">{name}</span>
              </a>
            );
          }

          return (
            <a
              key={`${url}-${fileIndex}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="safety-row-details__link"
              title={name}
            >
              {name}
            </a>
          );
        })}
      </div>
    </FieldShell>
  );
}

export function ImageLinkField({ label, column, raw }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [thumbErrors, setThumbErrors] = useState({});

  const urls = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const validUrls = urls.filter((url) => isHttpUrl(url));

  const closePreview = () => {
    setPreviewUrl("");
    setPreviewError(false);
    setPreviewLoading(false);
  };

  const openPreview = (url) => {
    setPreviewUrl(url);
    setPreviewError(false);
    setPreviewLoading(isImageUrl(url));
  };

  const markThumbError = (url) => {
    setThumbErrors((previous) => ({
      ...previous,
      [url]: true,
    }));
  };

  useEffect(() => {
    if (!previewUrl) return undefined;

    const handleEsc = (event) => {
      if (event.key === "Escape") closePreview();
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [previewUrl]);

  if (!validUrls.length) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <>
      <FieldShell
        label={label}
        column={column}
        className="safety-row-details__field--images"
      >
        <div className="safety-image-fields">
          <div className="safety-image-fields__meta">
            <span>
              {validUrls.length} photo{validUrls.length === 1 ? "" : "s"}{" "}
              present
            </span>
          </div>

          <div className="safety-image-fields__grid">
            {validUrls.map((url, imageIndex) => {
              const fileName =
                getFileName(url) || `Attachment ${imageIndex + 1}`;
              const isImage = isImageUrl(url);
              const hasThumbError = Boolean(thumbErrors[url]);

              return (
                <button
                  key={`${url}-${imageIndex}`}
                  type="button"
                  className={`safety-image-card ${
                    isImage
                      ? "safety-image-card--image"
                      : "safety-image-card--file"
                  }`}
                  onClick={() => openPreview(url)}
                  title={`Open ${fileName}`}
                >
                  <div
                    className={`safety-image-card__preview ${
                      isImage && hasThumbError
                        ? "safety-image-card__preview--photo-present"
                        : ""
                    }`}
                  >
                    {isImage && !hasThumbError ? (
                      <img
                        src={url}
                        alt={fileName}
                        loading="lazy"
                        onError={() => markThumbError(url)}
                      />
                    ) : isImage ? (
                      <div className="safety-image-card__photo-present">
                        <span>Photo Present</span>
                        <small>Click to view</small>
                      </div>
                    ) : (
                      <span className="safety-image-card__file-icon">
                        {fileExt(url)?.toUpperCase() || "FILE"}
                      </span>
                    )}
                  </div>

                  <div className="safety-image-card__info">
                    <strong>{fileName}</strong>
                    <span>
                      {isImage ? "Full panel clickable" : "Click to open file"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </FieldShell>

      {previewUrl ? (
        <div
          className="safety-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${label} preview`}
          onMouseDown={closePreview}
        >
          <div
            className="safety-preview-modal__box"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="safety-preview-modal__header">
              <div>
                <div className="safety-preview-modal__eyebrow">{label}</div>
                <div className="safety-preview-modal__title">
                  {getFileName(previewUrl) || "Attachment Preview"}
                </div>
              </div>

              <div className="safety-preview-modal__actions">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-preview-modal__action-btn"
                >
                  Open
                </a>

                <a
                  href={previewUrl}
                  download
                  className="safety-preview-modal__action-btn"
                >
                  Download
                </a>

                <button
                  type="button"
                  className="safety-preview-modal__close"
                  onClick={closePreview}
                  aria-label="Close preview"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="safety-preview-modal__body">
              {isImageUrl(previewUrl) && !previewError ? (
                <>
                  {previewLoading ? (
                    <div className="safety-preview-modal__loader">
                      Loading photo preview...
                    </div>
                  ) : null}

                  <img
                    className="safety-preview-modal__image"
                    src={previewUrl}
                    alt={label}
                    onLoad={() => setPreviewLoading(false)}
                    onError={() => {
                      setPreviewLoading(false);
                      setPreviewError(true);
                    }}
                  />
                </>
              ) : (
                <div className="safety-preview-modal__fallback">
                  <div className="safety-preview-modal__file-badge">
                    {isImageUrl(previewUrl)
                      ? "PHOTO"
                      : fileExt(previewUrl)?.toUpperCase() || "FILE"}
                  </div>

                  <h4>
                    {isImageUrl(previewUrl)
                      ? "Photo is present"
                      : "Preview not available"}
                  </h4>

                  <p>
                    The attachment exists, but the browser could not preview it
                    directly here. You can still open or download it.
                  </p>

                  <div className="safety-preview-modal__fallback-actions">
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="safety-preview-modal__open-link"
                    >
                      Open file
                    </a>

                    <a
                      href={previewUrl}
                      download
                      className="safety-preview-modal__download-link"
                    >
                      Download file
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}